export const PUBLIC_VIEW_STYLES_URL =
  "https://superplayerauction.com/new2025/styles.css";

let publicViewCssTextCache = null;
let publicViewCssTextPromise = null;

/** Fetch remote public-view CSS once; shared by preview inject + color defaults */
export function fetchPublicViewCssText() {
  if (publicViewCssTextCache) {
    return Promise.resolve(publicViewCssTextCache);
  }

  if (!publicViewCssTextPromise) {
    publicViewCssTextPromise = fetch(PUBLIC_VIEW_STYLES_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Failed to fetch public view stylesheet (${response.status})`,
          );
        }
        return response.text();
      })
      .then((text) => {
        publicViewCssTextCache = text;
        return text;
      })
      .catch((error) => {
        publicViewCssTextPromise = null;
        throw error;
      });
  }

  return publicViewCssTextPromise;
}

export const FONT_AWESOME_URL =
  "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css";

// Color CSS variable → React state key
export const CSS_VAR_TO_STATE = {
  "--ttl-clr": "color",
  "--plr-nmclr": "color2",
  "--plr-cd-txtclr": "color3",
  "--plr-cd-bgclr1": "color4",
  "--plr-cd-bgclr2": "color5",
  "--crnt-bdclr": "color6",

  "--tm-nm-txtclr": "color7",
  "--cr-plr-numclr": "color8",
  "--ttl-ptclr": "color9",
  "--mx-bdclr": "color10",
  "--rsv-ptclr": "color11",
};

// URL CSS variable → React state key (backgrounds; values are url(...))
export const CSS_URL_VAR_TO_STATE = {
  "--bg-img": "themebg",
};

// React state → color CSS variable (for themeVars prop)
export const STATE_TO_CSS_VAR = {
  color: "--ttl-clr",
  color2: "--plr-nmclr",
  color3: "--plr-cd-txtclr",
  color4: "--plr-cd-bgclr1",
  color5: "--plr-cd-bgclr2",
  color6: "--crnt-bdclr",

  color7: "--tm-nm-txtclr",
  color8: "--cr-plr-numclr",
  color9: "--ttl-ptclr",
  color10: "--mx-bdclr",
  color11: "--rsv-ptclr",
};

// React state → URL CSS variable (for themeVars prop)
export const STATE_TO_CSS_URL_VAR = {
  themebg: "--bg-img",
};

/** API custom.colors field → React state (public view wired pickers) */
export const API_COLOR_TO_STATE = {
  title_c: "color",
  plnm_c: "color2",
  plcod_c: "color3",
  plcodbg_c1: "color4",
  plcodbg_c2: "color5",
  crntbid_c: "color6",


  tm_nm_txtclr: "color7",
  cr_plr_numclr: "color8",
  ttl_ptclr: "color9",
  mx_bdclr: "color10",
  rsv_ptclr: "color11",
};

export const RESET_KEY_TO_CSS_VARS = {
  title: [{ cssVar: "--ttl-clr", stateKey: "color" }],
  player_name: [{ cssVar: "--plr-nmclr", stateKey: "color2" }],
  pl_code_clr: [{ cssVar: "--plr-cd-txtclr", stateKey: "color3" }],
  plcode_bg: [
    { cssVar: "--plr-cd-bgclr1", stateKey: "color4" },
    { cssVar: "--plr-cd-bgclr2", stateKey: "color5" },
  ],
  bid: [{ cssVar: "--crnt-bdclr", stateKey: "color6" }],

  teamName: [{ cssVar: "--tm-nm-txtclr", stateKey: "color7" }],
  curPl: [{ cssVar: "--cr-plr-numclr", stateKey: "color8" }],
  ttlpnt: [{ cssVar: "--ttl-ptclr", stateKey: "color9" }],
  mxbid: [{ cssVar: "--mx-bdclr", stateKey: "color10" }],
  resbid: [{ cssVar: "--rsv-ptclr", stateKey: "color11" }],
};

export const WIRED_COLOR_STATE_KEYS = Object.keys(STATE_TO_CSS_VAR);
export const WIRED_BG_CSS_VARS = Object.values(STATE_TO_CSS_URL_VAR);

export function parseCssColorVars(cssText) {
  const block = cssText.match(/:host\s*,\s*:root\s*\{([^}]+)\}/);
  if (!block) return {};

  const vars = {};
  for (const [, name, value] of block[1].matchAll(
    /--([\w-]+)\s*:\s*([^;]+)/g,
  )) {
    const v = value.trim();
    if (!v.startsWith("url(")) vars[`--${name}`] = v;
  }
  return vars;
}

/** Parse url(...) CSS variables from styles.css :host block */
export function parseCssUrlVars(cssText) {
  const block = cssText.match(/:host\s*,\s*:root\s*\{([^}]+)\}/);
  if (!block) return {};

  const vars = {};
  for (const [, name, value] of block[1].matchAll(
    /--([\w-]+)\s*:\s*([^;]+)/g,
  )) {
    const v = value.trim();
    if (v.startsWith("url(")) vars[`--${name}`] = v;
  }
  return vars;
}

/** Plain image path/URL → CSS url() value for --bg-img etc. */
export function toCssUrlValue(path) {
  if (!path) return "";
  const v = String(path).trim();
  if (v.startsWith("url(")) return v;
  return `url("${v}")`;
}

export function buildThemeVarsFromState(state, clearedCssVars = new Set()) {
  const themeVars = {};

  Object.entries(STATE_TO_CSS_VAR).forEach(([stateKey, cssVar]) => {
    if (clearedCssVars.has(cssVar)) return;
    const val = state[stateKey];
    if (val) themeVars[cssVar] = val;
  });

  Object.entries(STATE_TO_CSS_URL_VAR).forEach(([stateKey, cssVar]) => {
    if (clearedCssVars.has(cssVar)) return;
    const val = state[stateKey];
    const urlValue = toCssUrlValue(val);
    if (urlValue) themeVars[cssVar] = urlValue;
  });

  return themeVars;
}

/** Read default from parsed CSS file or live :host computed style on preview */
export function readCssDefaultForVar(cssVar, cssFileDefaults, host) {
  const fromFile = cssVarValueToHex(cssFileDefaults?.[cssVar]);
  if (fromFile) return fromFile;

  if (host && typeof getComputedStyle === "function") {
    const raw = getComputedStyle(host).getPropertyValue(cssVar).trim();
    const fromHost = cssVarValueToHex(raw);
    if (fromHost) return fromHost;
  }

  return "";
}

/** Picker display: live custom value, else CSS file / :host default */
export function getPickerColor(
  stateKey,
  stateValue,
  cssFileDefaults,
  host = null,
) {
  if (stateValue) return stateValue;

  const cssVar = STATE_TO_CSS_VAR[stateKey];
  if (!cssVar) return "#ffffff";

  return readCssDefaultForVar(cssVar, cssFileDefaults, host) || "#ffffff";
}

/**
 * Build state + cleared set from API colors.
 * Empty/missing API value → cleared (CSS file default in preview).
 */
export function buildPublicViewColorState(apiColors) {
  const cleared = new Set();
  const stateUpdates = {};

  Object.entries(API_COLOR_TO_STATE).forEach(([apiKey, stateKey]) => {
    const apiValue = apiColors?.[apiKey];
    const cssVar = STATE_TO_CSS_VAR[stateKey];
    const hasCustom = apiValue != null && String(apiValue).trim() !== "";

    stateUpdates[stateKey] = hasCustom ? String(apiValue).trim() : "";

    if (cssVar) {
      if (hasCustom) cleared.delete(cssVar);
      else cleared.add(cssVar);
    }
  });

  return { stateUpdates, cleared };
}

/** All wired colors cleared → CSS defaults only */
export function buildClearedPublicViewColorState() {
  return buildPublicViewColorState(null);
}

/** All wired colors + background cleared → full CSS file defaults in preview */
export function buildClearedPublicViewThemeState() {
  const { stateUpdates, cleared } = buildClearedPublicViewColorState();

  WIRED_BG_CSS_VARS.forEach((cssVar) => cleared.add(cssVar));

  return {
    stateUpdates: { ...stateUpdates, themebg: "" },
    cleared,
  };
}

/** Every CSS var managed by public-view customization (colors + bg) */
export function getAllPublicViewCssVars() {
  return [...Object.values(STATE_TO_CSS_VAR), ...WIRED_BG_CSS_VARS];
}

/**
 * API bg filename → custom preview bg, or cleared (CSS file --bg-img default).
 * bgFile: e.g. "27016241009155927780.jpg" from more[0].bg
 * bgBasePath: e.g. bgdata.path
 */
export function buildPublicViewBgState(bgFile, bgBasePath) {
  const hasCustom = bgFile != null && String(bgFile).trim() !== "";

  if (hasCustom && bgBasePath) {
    return {
      themebg: `${bgBasePath}${bgFile}`,
      cleared: false,
    };
  }

  return {
    themebg: "",
    cleared: true,
  };
}

/** Convert computed CSS color (hex, rgb, rgba) for color inputs */
export function cssVarValueToHex(value) {
  if (!value) return "";
  const v = value.trim();
  if (v.startsWith("#")) {
    return v.length === 4
      ? `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`
      : v.slice(0, 7);
  }

  const rgb = v.match(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*[\d.]+)?\s*\)/,
  );
  if (rgb) {
    const hex = (n) => Number(n).toString(16).padStart(2, "0");
    return `#${hex(rgb[1])}${hex(rgb[2])}${hex(rgb[3])}`;
  }

  return v;
}

/** Read mapped CSS variables from the shadow host element */
export function readHostCssVars(host) {
  if (!host) return {};

  const computed = getComputedStyle(host);
  const vars = {};

  Object.keys(CSS_VAR_TO_STATE).forEach((cssVar) => {
    const raw = computed.getPropertyValue(cssVar).trim();
    if (raw) vars[cssVar] = cssVarValueToHex(raw);
  });

  return vars;
}

/** Map CSS variable values to React color state keys */
export function cssVarsToStateUpdates(cssVars) {
  const updates = {};

  Object.entries(cssVars).forEach(([cssVar, value]) => {
    const stateKey = CSS_VAR_TO_STATE[cssVar];
    if (stateKey && value) updates[stateKey] = value;
  });

  return updates;
}

export function toHexColor(value) {
  if (!value) return "#ffffff";
  if (value.startsWith("#")) return value;
  return value;
}

/** Preview panel keys — add a block here when wiring a new preview region */
export const PREVIEW_BLOCKS = {
  auctionTiming: "auctionTiming",
  bidTimer: "bidTimer",
  currentPlayer: "currentPlayer",
  teamList: "teamList",
  teamsGrid: "teamsGrid",
  playersGrid: "playersGrid",
};

/** Default preview when no drawer is mapped */
export const DEFAULT_PREVIEW_SECTION = "currentPlayer";

/**
 * Left customization drawer id → preview layout id.
 * Add one line when a new drawer needs its own preview focus.
 */
export const DRAWER_TO_PREVIEW_SECTION = {
  timer: "timer",
  currentPlayer: "currentPlayer",
  teamList: "teamList",
  playerList: "playerList",
  // teamPlayers: "teamPlayers",
};

/**
 * Which preview blocks are visible per section.
 * Values are CSS display strings (block | flex | none).
 */
export const PREVIEW_SECTION_LAYOUT = {
  currentPlayer: {
    auctionTiming: "none",
    bidTimer: "flex",
    currentPlayer: "block",
    teamList: "block",
    teamsGrid: "grid",
    playersGrid: "none",
  },
  timer: {
    auctionTiming: "block",
    bidTimer: "none",
    currentPlayer: "none",
    teamList: "block",
    teamsGrid: "grid",
    playersGrid: "none",
  },
  teamList: {
    auctionTiming: "none",
    bidTimer: "flex",
    currentPlayer: "block",
    teamList: "block",
    teamsGrid: "grid",
    playersGrid: "none",
  },
  teamListFromTimer: {
    auctionTiming: "block",
    bidTimer: "none",
    currentPlayer: "none",
    teamList: "block",
    teamsGrid: "grid",
    playersGrid: "none",
  },
  teamPlayers: {
    auctionTiming: "none",
    bidTimer: "none",
    currentPlayer: "none",
    teamList: "block",
    teamsGrid: "none",
    playersGrid: "none",
  },
  playerList: {
    auctionTiming: "none",
    bidTimer: "flex",
    currentPlayer: "block",
    teamList: "block",
    teamsGrid: "none",
    playersGrid: "grid",
  },
  playerListFromTimer: {
    auctionTiming: "block",
    bidTimer: "none",
    currentPlayer: "none",
    teamList: "block",
    teamsGrid: "none",
    playersGrid: "grid",
  },
};

export function resolvePreviewSection(activeDrawer) {
  if (!activeDrawer) return DEFAULT_PREVIEW_SECTION;
  return (
    DRAWER_TO_PREVIEW_SECTION[activeDrawer] ?? DEFAULT_PREVIEW_SECTION
  );
}

export function resolveContextLayoutKey(
  previewSection,
  contextSource = "currentPlayer",
) {
  if (previewSection === "teamList") {
    return contextSource === "timer" ? "teamListFromTimer" : "teamList";
  }
  if (previewSection === "playerList") {
    return contextSource === "timer" ? "playerListFromTimer" : "playerList";
  }
  return previewSection;
}

/** @deprecated use resolveContextLayoutKey */
export function resolveTeamListLayoutKey(teamListSource) {
  return resolveContextLayoutKey("teamList", teamListSource);
}

export function getPreviewBlockDisplay(
  previewSection,
  blockKey,
  contextSource = "currentPlayer",
) {
  if (previewSection === "teamList" || previewSection === "playerList") {
    const layoutKey = resolveContextLayoutKey(previewSection, contextSource);
    const layout =
      PREVIEW_SECTION_LAYOUT[layoutKey] ??
      PREVIEW_SECTION_LAYOUT[previewSection];
    return layout[blockKey] ?? "none";
  }

  const layout =
    PREVIEW_SECTION_LAYOUT[previewSection] ??
    PREVIEW_SECTION_LAYOUT[DEFAULT_PREVIEW_SECTION];
  return layout[blockKey] ?? "none";
}

const DEFAULT_PREVIEW_REGION_STYLE = { opacity: 1, transform: "scale(1)" };
const DIMMED_PREVIEW_REGION_STYLE = { opacity: 0.6, transform: "scale(0.98)" };

/**
 * Per-drawer emphasis for top-level preview regions.
 * Add a region key here and apply it in PublicViewPreviewContent.
 */
const PREVIEW_REGION_STYLES = {
  currentPlayer: {
    playerSection: DEFAULT_PREVIEW_REGION_STYLE,
    teamsSection: DIMMED_PREVIEW_REGION_STYLE,
  },
  timer: {
    timerSection: DEFAULT_PREVIEW_REGION_STYLE,
    teamsSection: DIMMED_PREVIEW_REGION_STYLE,
  },
  teamList: {
    playerSection: DIMMED_PREVIEW_REGION_STYLE,
    teamsSection: DEFAULT_PREVIEW_REGION_STYLE,
    teamsGrid: DEFAULT_PREVIEW_REGION_STYLE,
  },
  teamListFromTimer: {
    timerSection: DIMMED_PREVIEW_REGION_STYLE,
    teamsSection: DEFAULT_PREVIEW_REGION_STYLE,
    teamsGrid: DEFAULT_PREVIEW_REGION_STYLE,
  },
  playerList: {
    playerSection: DIMMED_PREVIEW_REGION_STYLE,
    teamsSection: DEFAULT_PREVIEW_REGION_STYLE,
    playersGrid: DEFAULT_PREVIEW_REGION_STYLE,
  },
  playerListFromTimer: {
    timerSection: DIMMED_PREVIEW_REGION_STYLE,
    teamsSection: DEFAULT_PREVIEW_REGION_STYLE,
    playersGrid: DEFAULT_PREVIEW_REGION_STYLE,
  },
};

export function getPreviewRegionStyle(
  previewSection,
  region,
  emphasizeRegions = true,
  contextSource = "currentPlayer",
) {
  if (!emphasizeRegions) {
    return DEFAULT_PREVIEW_REGION_STYLE;
  }

  if (previewSection === "teamList" || previewSection === "playerList") {
    const layoutKey = resolveContextLayoutKey(previewSection, contextSource);
    return (
      PREVIEW_REGION_STYLES[layoutKey]?.[region] ??
      PREVIEW_REGION_STYLES[previewSection]?.[region] ??
      DEFAULT_PREVIEW_REGION_STYLE
    );
  }

  return (
    PREVIEW_REGION_STYLES[previewSection]?.[region] ??
    DEFAULT_PREVIEW_REGION_STYLE
  );
}
